import { useEffect, useMemo, useState } from "react";
import { ExternalLink, Heart, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { track } from "@/lib/analytics";
import { Reveal, Overline } from "@/components/Reveal";

const FavoriteCard = ({ item }) => (
  <div
    data-testid={`favorite-${item.fav_id}`}
    className="flex h-full flex-col overflow-hidden rounded-[2rem] border border-forest/10 bg-white/75 shadow-[0_14px_35px_rgba(23,63,53,0.08)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_22px_50px_rgba(23,63,53,0.14)]"
  >
    {item.image && (
      <div className="aspect-[4/3] w-full overflow-hidden bg-sage/40">
        <img
          src={item.image}
          alt={`${item.name}${item.brand ? ` by ${item.brand}` : ""}`}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      </div>
    )}
    <div className="flex flex-1 flex-col p-6">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold-dark">
          {item.category}
        </p>
        {item.featured && <Heart className="h-4 w-4 fill-gold text-gold" aria-label="Featured" />}
      </div>
      <h3 className="mt-2 font-serif text-xl leading-snug text-forest">{item.name}</h3>
      {item.brand && <p className="mt-0.5 text-xs font-semibold text-taupe">{item.brand}</p>}
      {item.description && (
        <p className="mt-2 text-sm leading-relaxed text-charcoal/70">{item.description}</p>
      )}
      {item.note && (
        <p className="mt-3 border-l-2 border-gold/50 pl-4 font-serif text-sm italic leading-relaxed text-forest/85">
          "{item.note}"
        </p>
      )}
      <a
        href={item.url}
        target="_blank"
        rel="sponsored noopener noreferrer"
        data-testid={`favorite-visit-${item.fav_id}`}
        onClick={() => track("affiliate_click", { item: item.fav_id })}
        className="mt-auto inline-flex w-fit items-center gap-2 pt-5 text-sm font-semibold text-forest underline-offset-4 transition-colors hover:text-gold-dark hover:underline"
      >
        Visit Website
        <ExternalLink className="h-4 w-4" />
      </a>
    </div>
  </div>
);

export default function Favorites() {
  const [data, setData] = useState(null);
  const [category, setCategory] = useState("");

  useSEO(
    "Staci's Favorites | Rooted Healing",
    "Books, tools, and resources personally recommended by Staci Freed of Rooted Healing."
  );

  useEffect(() => {
    track("favorites_view");
    api.get("/content/favorites").then(({ data }) => setData(data)).catch(() => setData({ items: [], categories: [] }));
  }, []);

  const visible = useMemo(() => {
    if (!data) return [];
    return data.items.filter((i) => !category || i.category === category);
  }, [data, category]);

  return (
    <main data-testid="favorites-page" className="pt-[76px]">
      <section className="mx-auto max-w-5xl px-5 py-16 text-center sm:px-8 sm:py-20">
        <Reveal>
          <Overline>Curated by Staci</Overline>
          <h1 className="mt-4 font-serif text-4xl leading-tight text-forest sm:text-5xl">
            Staci's <em className="italic text-gold">Favorites</em>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg font-light leading-relaxed text-charcoal/70">
            Books, tools, and everyday resources Staci personally uses and recommends, gathered
            in one place.
          </p>
          <p
            data-testid="affiliate-disclosure"
            className="mx-auto mt-6 max-w-2xl rounded-2xl border border-gold/30 bg-sage/60 px-6 py-4 text-xs leading-relaxed text-charcoal/75"
          >
            Some links on this page are affiliate links. If you make a purchase through these
            links, I may earn a commission at no additional cost to you. Recommendations are
            personal and educational; nothing here is a medical claim, and nothing on this page
            is required for your wellness.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-6xl px-5 pb-24 sm:px-8">
        {data && data.items.length > 0 && (
          <div className="mb-10 flex flex-wrap justify-center gap-2">
            <button
              data-testid="favorites-filter-all"
              onClick={() => setCategory("")}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                !category ? "bg-forest text-ivory" : "border border-forest/20 text-forest hover:border-gold"
              }`}
            >
              All
            </button>
            {data.categories.map((c) => (
              <button
                key={c}
                data-testid={`favorites-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setCategory(category === c ? "" : c)}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                  category === c
                    ? "bg-forest text-ivory"
                    : "border border-forest/20 text-forest hover:border-gold"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {!data ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-forest" />
          </div>
        ) : visible.length === 0 ? (
          <div className="mx-auto max-w-xl rounded-[2rem] border border-forest/10 bg-white/75 p-10 text-center">
            <p className="font-serif text-2xl text-forest">Recommendations are coming soon</p>
            <p className="mt-3 text-sm leading-relaxed text-charcoal/70">
              Staci is gathering her favorite resources. Check back shortly.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((item) => (
              <FavoriteCard key={item.fav_id} item={item} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
